import React from "react";
import styled from "@emotion/styled";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import {
  Badge,
  BadgeVariant as BadgeColorVariant,
  Card,
  GainLossTypography,
} from "@components";
import { SecurityImage } from "@components/SecurityImage";
import { toCurrency } from "@helpers";
import { BadgePaletteSlot, TimelineArticle } from "./types";

const LOGO_SIZE = 32;
const DESCRIPTION_MAX_LINES = 2;

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.border.secondary}`,
  borderRadius: 8,
  boxShadow: theme.shadows[2],
  gap: theme.spacing(4),
}));

const toBadgeColor = (slot: BadgePaletteSlot): BadgeColorVariant =>
  slot === "neutral" ? "default" : slot;

const badgeTextColor = (theme: Theme, slot: BadgePaletteSlot): string =>
  slot === "neutral" ? theme.palette.text.secondary : theme.palette[slot].dark;

const HeaderRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
});

const BadgeLabel = styled(Typography)({
  textTransform: "uppercase",
});

const ContentRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  alignItems: "flex-start",
  width: "100%",
}));

const MainColumn = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const Description = styled(Typography)(({ theme }) => ({
  paddingLeft: `calc(${LOGO_SIZE}px + ${theme.spacing(1.5)})`,
  display: "-webkit-box",
  WebkitLineClamp: DESCRIPTION_MAX_LINES,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const SideColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
});

const BottomLine = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  textTransform: "uppercase",
}));

const CardLink = styled("a")({
  display: "block",
  textDecoration: "none",
  color: "inherit",
});

const recommendationSign = (recommendation: string): number => {
  const normalized = recommendation.toLowerCase();
  if (normalized === "kaufen") return 1;
  if (normalized === "verkaufen") return -1;
  return 0;
};

interface TimelineCardProps {
  item: TimelineArticle;
}

export const TimelineCard = ({ item }: TimelineCardProps) => {
  const theme = useTheme();
  const { recommendation, price } = item;
  const hasRating = recommendation !== undefined && price !== undefined;

  const { paletteSlot, variant, fontVariant } = item.badge;
  const isOutlined = variant === "outlined";
  const borderColor =
    paletteSlot === "neutral"
      ? theme.palette.border.secondary
      : theme.palette[paletteSlot].main;
  const textColor = badgeTextColor(theme, paletteSlot);

  const card = (
    <StyledCard size="maxi">
      <HeaderRow>
        <Badge
          variant={toBadgeColor(paletteSlot)}
          sx={{
            height: 24,
            borderRadius: "4px",
            py: 0.5,
            ...(isOutlined && {
              backgroundColor: "transparent",
              border: `1px solid ${borderColor}`,
            }),
          }}
        >
          <BadgeLabel variant={fontVariant} color={textColor}>
            {item.categoryLabel}
          </BadgeLabel>
        </Badge>
      </HeaderRow>

      <ContentRow>
        <MainColumn>
          <TitleRow>
            <SecurityImage
              src={item.logoUrl}
              alt={item.title}
              width={LOGO_SIZE}
              height={LOGO_SIZE}
            />
            <Typography variant="body4">{item.title}</Typography>
          </TitleRow>
          {item.description && (
            <Description variant="body3" color="textSecondary">
              {item.description}
            </Description>
          )}
        </MainColumn>

        {hasRating && (
          <SideColumn>
            <GainLossTypography
              variant="subtitle2"
              value={recommendationSign(recommendation)}
              showIndicator={recommendationSign(recommendation) !== 0}
            >
              {toCurrency(price, "EUR")}
            </GainLossTypography>
            <BottomLine variant="overline2">{recommendation}</BottomLine>
          </SideColumn>
        )}
      </ContentRow>

      <BottomLine variant="overline2">
        {item.isin || `MAKRO / ${item.country ?? ""}`}
      </BottomLine>
    </StyledCard>
  );

  if (!item.href) {
    return card;
  }

  return (
    <CardLink href={item.href} target="_blank" rel="noopener noreferrer">
      {card}
    </CardLink>
  );
};
